import React from 'react';
import { Menu, MenuItem } from '@progress/kendo-react-layout';
import { Popup } from '@progress/kendo-react-popup';
import { AccentTextUnbound } from './AccentTextUnBound';
import { AccentTextAreaUnbound } from './AccentTextAreaUnbound';
import { accentUtils, t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();
const safeItems = (v) => (Array.isArray(v) ? v : []);

export const AccentTemplateInputUnbound = ({
  templateCatalog,
  onChange,
  defaultValue,
  disabled,
  insertMode = 'token',
  multiline = false,
  ...inputProps
}) => {
  const text = safeString(defaultValue);

  const hasTemplates = React.useMemo(() => {
    const list = Array.isArray(templateCatalog) ? templateCatalog : [];
    return list.some(g => (g?.items?.length ?? 0) > 0);
  }, [templateCatalog]);

  const [showContext, setShowContext] = React.useState(false);
  const [offset, setOffset] = React.useState({ left: 0, top: 0 });

  const lastTargetRef = React.useRef(null);
  const lastSelRef = React.useRef({ start: null, end: null });
  const currentValueRef = React.useRef(text);

  React.useEffect(() => {
    currentValueRef.current = text;
  }, [text]);

  const closeContext = React.useCallback(() => setShowContext(false), []);

  React.useEffect(() => {
    if (!showContext) return;

    const onMouseDown = (e) => {
      try {
        if (e?.target?.closest?.('.acc-template-menu')) return;
      } catch {
        // ignore
      }
      closeContext();
    };

    const onKeyDown = (e) => {
      if ((e?.key ?? '') === 'Escape') closeContext();
    };

    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [closeContext, showContext]);

  const insertSnippet = React.useCallback((snippet) => {
    const valueToInsert = safeString(snippet);
    if (accentUtils.isEmpty(valueToInsert)) return;

    const cur = safeString(currentValueRef.current);
    const sel = lastSelRef.current ?? {};
    let start = Number(sel.start);
    let end = Number(sel.end);

    if (!Number.isFinite(start) || start < 0) start = cur.length;
    if (!Number.isFinite(end) || end < 0) end = start;
    if (end < start) end = start;

    const next = cur.slice(0, start) + valueToInsert + cur.slice(end);
    currentValueRef.current = next;
    onChange?.(next);

    const el = lastTargetRef.current;
    const pos = start + valueToInsert.length;
    window.setTimeout(() => {
      try {
        if (el && typeof el.value !== 'undefined') {
          el.value = next;
        }
        el?.focus?.();
        el?.setSelectionRange?.(pos, pos);
      } catch {
        // ignore
      }
    }, 0);
  }, [onChange]);

  const onTemplateSelect = React.useCallback((e) => {
    const data = e?.item?.data;
    const snippet = safeString(data?.snippet ?? data?.id);
    closeContext();
    if (!accentUtils.isEmpty(snippet)) {
      insertSnippet(snippet);
    }
  }, [closeContext, insertSnippet]);

  const renderMenuItems = React.useCallback((items, parentKey) => {
    return safeItems(items).map((it, ii) => {
      const tok = safeString(it?.token).trim();
      const lbl = safeString(it?.label).trim();
      const snip = safeString(it?.snippet).trim();
      const children = safeItems(it?.items);

      if (tok) {
        const text = lbl || tok;
        const snippet =
          insertMode === 'snippet'
            ? (snip || tok)
            : tok;
        return (
          <MenuItem
            key={`${parentKey}_leaf_${ii}_${tok}`}
            text={text}
            data={{ id: tok, snippet }}
          />
        );
      }

      if (children.length > 0) {
        const text = lbl || t('Templates');
        return (
          <MenuItem key={`${parentKey}_grp_${ii}_${text}`} text={text}>
            {renderMenuItems(children, `${parentKey}_${ii}`)}
          </MenuItem>
        );
      }

      return null;
    }).filter(Boolean);
  }, [insertMode]);

  const InputComponent = multiline ? AccentTextAreaUnbound : AccentTextUnbound;

  return (
    <>
      {hasTemplates && (
        <Popup show={showContext} offset={offset}>
          <Menu
            className="acc-template-menu"
            vertical
            style={{ display: 'inline-block' }}
            onSelect={onTemplateSelect}
          >
            {(templateCatalog ?? []).map((g, gi) => {
              const groupName = safeString(g?.group) || t('Templates');
              const items = safeItems(g?.items);
              if (items.length === 0) return null;

              return (
                <MenuItem key={`grp_${gi}_${groupName}`} text={groupName}>
                  {renderMenuItems(items, `grp_${gi}`)}
                </MenuItem>
              );
            }).filter(Boolean)}
          </Menu>
        </Popup>
      )}

      <InputComponent
        {...inputProps}
        disabled={!!disabled}
        defaultValue={text}
        resetWhenDefaultValueChanges={!multiline}
        onChange={(value) => {
          currentValueRef.current = safeString(value);
          onChange?.(value);
        }}
        onContextMenu={(e) => {
          if (!hasTemplates) return;
          if (disabled) return;

          try {
            if (e?.preventDefault) e.preventDefault();
          } catch {
            // ignore
          }

          const tgt = e?.target;
          lastTargetRef.current = tgt;
          lastSelRef.current = {
            start: tgt?.selectionStart,
            end: tgt?.selectionEnd,
          };

          setOffset({ left: e?.clientX ?? 0, top: e?.clientY ?? 0 });
          setShowContext(true);
        }}
      />
    </>
  );
};
